import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FolderPlus, FileText, BarChart3, HardHat, ShieldCheck, Building2, ClipboardList, ClipboardCheck, CalendarRange, Users, FileSignature, MessagesSquare, AlertTriangle, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import CreateAssessmentDialog from "../../CreateAssessmentDialog";
import CreateFolderDialog from "../../CreateFolderDialog";
import { CardActionItem } from "./CardActionItem";
import { ActionGroup } from "./ActionGroup";
import { RiskPhase } from "@/lib/riskPhase";
import { cn } from "@/lib/utils";

export type ActionBarGroup = "amp" | "basp" | "basu" | "gemensamt" | "organisera";

interface ModernActionBarProps {
  projectId: string;
  folderId: string;
  isShared: boolean;
  onStartAssessment?: (projectId: string, folderId: string) => void;
  onCreateFolder: (name: string, description?: string) => Promise<void>;
  onRefresh?: () => void;
  /** Resolves a folder to place new content in when standing at project level */
  ensureFolderId?: () => Promise<string | null>;
  /** Which groups of actions to render. Defaults to all. */
  groups?: ActionBarGroup[];
  /** Optional heading override */
  heading?: string;
  headingDescription?: string;
  /** Dokumentläge: kopplat till arbetsmiljöplanen eller fristående */
  mode?: "amp" | "standalone";
  /** Gör hela blocket hopfällbart (används på projektöversikten) */
  collapsible?: boolean;
  /** Kontrollerat öppet-läge när collapsible är aktivt */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Vald flik när flera faser visas */
  activeTab?: string;
  onActiveTabChange?: (tab: string) => void;
}

export const ModernActionBar: React.FC<ModernActionBarProps> = ({
  projectId,
  folderId,
  isShared,
  onStartAssessment,
  onCreateFolder,
  onRefresh,
  ensureFolderId,
  groups = ["amp", "basp", "basu", "gemensamt", "organisera"],
  heading = "Alla dokument",
  headingDescription = "Skapa direkt i projektet – mappar är valfria för att organisera senare",
  mode = "amp",
  collapsible = false,
  open,
  onOpenChange,
  activeTab,
  onActiveTabChange
}) => {
  const show = (g: ActionBarGroup) => groups.includes(g);
  const isStandalone = mode === "standalone";
  const navigate = useNavigate();
  const [isCreateAssessmentDialogOpen, setIsCreateAssessmentDialogOpen] = useState(false);
  const [isCreateFolderDialogOpen, setIsCreateFolderDialogOpen] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [activeFolderId, setActiveFolderId] = useState<string>(folderId);
  const [isResolving, setIsResolving] = useState(false);
  const [assessmentPhase, setAssessmentPhase] = useState<RiskPhase>("byggskede");
  const [internalOpen, setInternalOpen] = useState(!collapsible);
  const [internalTab, setInternalTab] = useState<string>("basp");

  const isOpen = collapsible ? (open ?? internalOpen) : true;
  const setOpen = (next: boolean) => {
    onOpenChange?.(next);
    if (open === undefined) setInternalOpen(next);
  };

  // Faser som kan väljas mellan (endast när fler än en finns)
  const phaseTabs = (["basp", "basu", "gemensamt"] as const).filter((g) => show(g));
  const useTabs = phaseTabs.length > 1;
  const selectedTab = (activeTab ?? internalTab) as "basp" | "basu" | "gemensamt";
  const currentTab = phaseTabs.includes(selectedTab) ? selectedTab : phaseTabs[0];
  const setTab = (tab: string) => {
    onActiveTabChange?.(tab);
    if (activeTab === undefined) setInternalTab(tab);
  };
  const showPhase = (g: "basp" | "basu" | "gemensamt") =>
    show(g) && (!useTabs || currentTab === g);

  const tabLabels: Record<string, string> = {
    basp: "Planera och projektera",
    basu: "Ute på arbetsplatsen",
    gemensamt: "Gemensamt"
  };

  const canCreateContent = Boolean(folderId) || Boolean(ensureFolderId);

  const resolveFolderId = async (): Promise<string | null> => {
    if (folderId) return folderId;
    if (!ensureFolderId) return null;
    setIsResolving(true);
    try {
      return await ensureFolderId();
    } finally {
      setIsResolving(false);
    }
  };

  const handleCreateAssessmentSuccess = () => {
    setIsCreateAssessmentDialogOpen(false);
    onRefresh?.();
  };

  const handleCreateFolderSubmit = async () => {
    if (!folderName.trim()) return;
    await onCreateFolder(folderName.trim());
    setFolderName("");
    setIsCreateFolderDialogOpen(false);
    onRefresh?.();
  };

  const handleCreateRiskAssessment = async (phase: RiskPhase) => {
    const target = await resolveFolderId();
    if (!target) return;
    setAssessmentPhase(phase);
    setActiveFolderId(target);
    setIsCreateAssessmentDialogOpen(true);
  };

  const handleCreateSKRAssessment = async () => {
    const target = await resolveFolderId();
    if (!target) return;
    navigate(`/skr-bedomning/${projectId}/${target}?createNew=true`);
  };

  const handleCreateWorkPreparation = async () => {
    const target = await resolveFolderId();
    if (!target) return;
    navigate(`/arbetsberedning/${projectId}/${target}`);
  };

  const handleCreateWorkArea = () => {
    navigate(`/arbetsomrade/${projectId}`);
  };

  const handleCreateSafetyRules = () => {
    navigate(`/skyddsregler/${projectId}`);
  };

  const openSamordning = (flik: string) => {
    navigate(`/samordning/${projectId}?flik=${flik}`);
  };

  return (
    <>
      <Card className="mb-6 border border-border/60 bg-muted/30 shadow-none animate-fade-in">
        <CardContent className="p-5 sm:p-6 space-y-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                Verktyg
              </span>
              <h2 className="text-base font-semibold text-foreground mt-1">
                {heading}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {headingDescription}
              </p>
              {!isStandalone ? null : (
                <p className="text-xs text-muted-foreground mt-2">
                  Fristående läge – endast dokument som fungerar utan arbetsmiljöplan visas.
                </p>
              )}
            </div>

            {collapsible && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setOpen(!isOpen)}
                aria-expanded={isOpen}
                className="shrink-0 gap-1.5"
              >
                {isOpen ? "Dölj" : "Visa"}
                <ChevronDown
                  className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")}
                  aria-hidden="true"
                />
              </Button>
            )}
          </div>

          {isOpen && (
          <div className="space-y-6">

            {canCreateContent && useTabs && (
              <div className="flex flex-wrap gap-2">
                {phaseTabs.map((tab) => (
                  <Button
                    key={tab}
                    type="button"
                    size="sm"
                    variant={currentTab === tab ? "default" : "outline"}
                    onClick={() => setTab(tab)}
                  >
                    {tabLabels[tab]}
                  </Button>
                ))}
              </div>
            )}

            {canCreateContent && (
              <>
                {show("amp") && (!useTabs || currentTab === "basp") && (
                <ActionGroup
                  label="Arbetsmiljöplanen"
                  description="Hör direkt till planen"
                  tone="basp"
                  columns={3}
                >
                  <CardActionItem
                    icon={ClipboardList}
                    docNumber="Dok 15"
                    title="Regler på arbetsplatsen"
                    description="Så vet alla på plats vad som gäller."
                    onClick={handleCreateSafetyRules}
                  />
                </ActionGroup>
                )}

                {showPhase("basp") && (
                <ActionGroup
                  label="Planera och projektera"
                  description="Bas-P – underlag innan arbetet startar"
                  tone="basp"
                  columns={3}
                >
                  <CardActionItem
                    icon={ShieldCheck}
                    docNumber="Dok 10"
                    title="Utred arbetsområdet"
                    description="Grunden – visar platsens förutsättningar och risker."
                    onClick={handleCreateWorkArea}
                  />

                  <CardActionItem
                    icon={FileText}
                    docNumber="Dok 30b"
                    title="Risker under byggtiden"
                    description="Samlar de risker som ska hanteras medan arbetet pågår."
                    onClick={() => handleCreateRiskAssessment("byggskede")}
                  />

                  {!isStandalone && (
                    <CardActionItem
                      icon={Building2}
                      docNumber="Dok 31"
                      title="Kvarstående risker"
                      description="Risker som finns kvar vid drift och underhåll."
                      onClick={() => handleCreateRiskAssessment("bruksskede")}
                    />
                  )}

                  <CardActionItem
                    icon={BarChart3}
                    docNumber="Dok 03"
                    title="SKR-bedömning"
                    description="Bedömer projektets storlek, komplexitet och risk."
                    onClick={handleCreateSKRAssessment}
                  />

                  <CardActionItem
                    icon={ClipboardCheck}
                    docNumber="Dok 33"
                    title="Uppföljning bruksskede"
                    description="Bekräftar att arbetsplatsens utformning håller vid drift."
                    onClick={() => openSamordning("bruksuppfoljning")}
                  />

                  <CardActionItem
                    icon={FileSignature}
                    docNumber="Dok 34"
                    title="Överlämningssammanfattning"
                    description="Sammanfattar vad som lämnas över inför bruksskedet."
                    onClick={() => openSamordning("overlamning")}
                  />

                </ActionGroup>
                )}

                {showPhase("basu") && (
                <ActionGroup
                  label="Ute på arbetsplatsen"
                  description="Bas-U – när arbetet pågår"
                  tone="basu"
                  columns={3}
                >
                  <CardActionItem
                    icon={HardHat}
                    docNumber="Dok 24"
                    title="Arbetsberedning"
                    description="Beskriver hur ett riskfyllt moment utförs säkert."
                    onClick={handleCreateWorkPreparation}
                  />

                  <CardActionItem
                    icon={CalendarRange}
                    docNumber="Dok 26"
                    title="Skedesplan / APD-plan"
                    description="Visar skeden, samtidiga arbeten och gemensamma skydd."
                    onClick={() => openSamordning("skedesplan")}
                  />

                  <CardActionItem
                    icon={FileSignature}
                    docNumber="Dok 20 / 22"
                    title="Överlämning"
                    description="Från Bas-P till Bas-U och vidare till byggherren."
                    onClick={() => openSamordning("overlamning")}
                  />

                  <CardActionItem
                    icon={HardHat}
                    docNumber="Dok 27"
                    title="Skyddsrond"
                    description="Protokollför kontrollen av arbetsplatsen."
                    onClick={() => openSamordning("skyddsrond")}
                  />

                  <CardActionItem
                    icon={AlertTriangle}
                    docNumber="Dok 28"
                    title="Tillbud och olyckor"
                    description="Rapportera och utred händelser enligt AFS 2023:1."
                    onClick={() => openSamordning("tillbud")}
                  />
                </ActionGroup>
                )}

                {showPhase("gemensamt") && (
                <ActionGroup
                  label="Gemensamt"
                  description="När projektering och produktion pågår samtidigt"
                  tone="neutral"
                  columns={3}
                >
                  <CardActionItem
                    icon={Users}
                    docNumber="Dok 23"
                    title="Samordningsmöte"
                    description="Protokoll över risker, ändringar och beslut."
                    onClick={() => openSamordning("samordning")}
                  />

                  <CardActionItem
                    icon={MessagesSquare}
                    docNumber="Dok 25"
                    title="Q&A-logg"
                    description="Håller reda på frågor, svar, ansvarig och status."
                    onClick={() => openSamordning("qalogg")}
                  />
                </ActionGroup>
                )}
              </>
            )}

            {show("organisera") && (
              <div className="pt-1 border-t border-border/40">
                <Button
                  variant="link"
                  size="sm"
                  className="px-0 text-muted-foreground hover:text-foreground"
                  onClick={() => setIsCreateFolderDialogOpen(true)}
                >
                  <FolderPlus className="mr-1.5 h-4 w-4" aria-hidden="true" />
                  Skapa mapp (valfritt)
                </Button>
              </div>
            )}
          </div>
          )}

          {isResolving && (
            <p className="text-sm text-muted-foreground">Förbereder…</p>
          )}

        </CardContent>
      </Card>

      {activeFolderId && (
        <CreateAssessmentDialog
          isOpen={isCreateAssessmentDialogOpen}
          onOpenChange={setIsCreateAssessmentDialogOpen}
          projectId={projectId}
          folderId={activeFolderId}
          onSuccess={handleCreateAssessmentSuccess}
          isSharedFolder={isShared}
          phase={assessmentPhase}
        />
      )}

      <CreateFolderDialog
        isOpen={isCreateFolderDialogOpen}
        onOpenChange={setIsCreateFolderDialogOpen}
        onSubmit={handleCreateFolderSubmit}
        folderName={folderName}
        setFolderName={setFolderName}
      />
    </>
  );
};
